/* eslint-disable max-lines */
// deps
import React, { memo, useCallback, useMemo, useState, useContext, useEffect } from "react";

// internal-deps
import { SwiggyOneWidget } from "@swiggy-private/super-ui";
import { getCloudinaryUrl, isEmpty } from "@swiggy-private/js-utils";
import { BillDetails } from "@swiggy-private/dineout-payment-success-ui";
import ListView from "@swiggy-private/dls-web-core/dist/components/ListView";
import { IconName } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb/types";
import { IColors, ITypography } from "@swiggy-private/dls-web-common/dist/components/types.internal";
import { ExpendableType } from "@swiggy-private/dls-web-core/dist/components/ListView/types";
import { FullWidthButton } from "@swiggy-private/dls-web-core/dist/components/FullWidthButton";
import { Popup } from "@swiggy-private/dls-web-core/dist/components/Popup";
import { TableBookingInfoTicket } from "@swiggy-private/dls-web-recipe-do-web/dist/components/TableBookingInfoTicket";
import { RadioCardGroup } from "@swiggy-private/dls-web-recipe-do-web/dist/components/RadioCardGroup";
import { CartItemListRenderer } from "@swiggy-private/dls-web-recipe-do-web/dist/components/CartItemListRenderer";
import {
    ErrorWidget,
    Header,
    SavingWidget,
    CouponSection,
    AlertBanner,
    RewardsWidget,
    DinecashEarningsNudge,
} from "@swiggy-private/dineout-cart-ui";
import { TurboPaymentWidgetShimmerV2 } from "@swiggy-private/payment-ui";
import { Loader } from "@swiggy-private/dls-web-core/dist/components/Loader";
import TableReservationShimmer from "../../components/shimmer";
import {
    CartModal,
    CouponMessageCard,
    ElementVisibilitySensor,
    ErrorFooterWidget,
    format,
    GenericError,
    isWebPlatform,
    useEffectOnce,
} from "@swiggy-private/core-ui";
import {
    closeWebview,
    GlobalHeaderState,
    navigateToDeeplink,
    SwgyHeaderKeysList,
    ITicketData,
    ISuperSavingBottomWidgetProps,
    IWidget,
    IApplyCouponWidget,
    IPartialPaymentWidget,
    recordNREvent,
    NEWRELIC_TABLES,
    IHighlightedFooterCallout,
    DineoutCartType,
    DineoutCartContext,
} from "@swiggy-private/checkout-core";
import { IRewardsWidget } from "@swiggy-private/checkout-types";

// interfaces
import {
    IBillEntries,
    IBookingTicketMeta,
    ICartPageErrorPayload,
    ICartSelectedSlot,
    IOnImpressionBillDetailsProps,
    IRewardWidgetResponseData,
} from "@dineout-cart-client/interfaces";
import { IEventTicketDetails, IHeaderData } from "@dineout-cart-client/interfaces/Events";

// contexts
import { UpdateCartContext } from "@dineout-cart-client/contexts/UpdateCartContext";

// utils
import {
    getCTAText,
    getBillEntriesList,
    getUpsellWidgetProps,
    getPopupData,
    getTicketDetailsIdList,
} from "@dineout-cart-client/utils/TableReservationUtils";
import { appendThemeToExternalWidgetUrl } from "@dineout-cart-client/helpers/CheckoutThemes";
import {
    getIsRewardRedeemed,
    getRedeemableRewards,
    getRewardsTogglePayload,
} from "@dineout-cart-client/utils/RewardsWidgetUtils";
import {
    areAllItemsUnavailable,
    getUnavailableItemsCount,
    isAnyItemUnavailable,
} from "@dineout-cart-client/utils/EventBooking";

// constants
import {
    BILL_DETAILS_TEXTS,
    CART_PAGE_TEXTS,
    CART_TEST_IDS,
    CTA_BUTTON_TEXT,
    DOWNLOAD_APP_BANNER_SUBTITLE,
    DOWNLOAD_APP_BANNER_TITLE,
    UNAVAILABLE_ITEMS_CTA_SUBTEXT,
    VALIDATION_LOADER_TEXT,
    PARTIAL_PAYMENT_RADIO_GROUP,
    PAYMENT_OPTION_IDS,
    VALIDATION_LOADER_POPUP_WIDTH,
} from "@dineout-cart-client/constants";
import { CART_STATUS_CODES } from "@dineout-cart-client/constants/Error";
import { getErrorWidgetAttrs } from "@dineout-cart-client/helpers/Error";
import { CART_ERROR_CTA } from "@swiggy-private/cart-types";
import { ErrorWidget as CartErrorWidget } from "@swiggy-private/core-ui";

// styles
import {
    ContentContainer,
    CTASubText,
    ProceedCtaWrapper,
    SwiggyOneWidgetWrapper,
    TicketDetailsWrapper,
    SavingWidgetWrapper,
    DividerWrapper,
    ListViewWithBorder,
    PartialPaymentSection,
} from "./styles";

//hooks
import useDineoutCartActions from "@dineout-cart-client/hooks/useDineoutCartActions";
import useDineoutCartItemActions from "@dineout-cart-client/hooks/useDineoutCartItemActions";
import { IUpdateItemProp, useDineoutBookingAnalytics } from "@dineout-cart-client/hooks/useDineoutCartAnalytics";
import { createAndPersistPaymentLink } from "@dineout-cart-client/helpers/PaymentLink";
// components
import OfferTnC from "./OfferTnC";
import ThingsToRemember from "./ThingsToRemember";
import SpecialRequest from "./SpecialRequest";
import BenefitsHalfcard from "../benefits-halfcard";
import { FooterContainer } from "@dineout-cart-client/components/cart-footer/styles";
import { ValidationLoadingContainer } from "@dineout-cart-client/components/table-reservation/styles";
import { ILocalCartItem } from "@dineout-cart-client/interfaces/LocalCart";
import { DINEOUT_CART_MODAL_CLOSE_ICON_BG, isDineoutCheckoutTheme } from "@dineout-cart-client/helpers/CheckoutThemes";
import {
    IImpressionCashBackBenefitCallout,
    IImpressionDisclaimerBanner,
} from "@dineout-cart-client/interfaces/AnalyticsEvents";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { ITableBookingCartPage } from "@dineout-cart-client/analytics/events";
import { TurboPayment } from "@swiggy-private/payment-client";

export interface ICtaSubTextFromCallouts {
    text?: string;
    icon?: string;
}

interface IContentProps {
    isInitialCartLoading?: boolean;
    isEventsBookingFlow?: boolean;
    cartType: DineoutCartType;
    cartTypeFromLocalCart?: string;
    savingWidgetData?: IWidget;
    specialRequestWidgetData?: IWidget;
    headerData: IHeaderData;
    eventTicketDetails?: IEventTicketDetails;
    billEntries: IBillEntries[];
    bookingTicketMeta?: IBookingTicketMeta;
    billAmount?: number;
    skipPayment?: boolean;
    ticketData?: ITicketData;
    dealCategory?: string;
    superSavingBottomWidget?: ISuperSavingBottomWidgetProps;
    ids: { restaurantId: string; eventId: string };
    restaurantId?: string;
    selectedOfferId?: string;
    selectedSlot?: ICartSelectedSlot;
    clickEventOnConfirmBookingTable: () => void;
    onImpressionBillDetails: (props: IOnImpressionBillDetailsProps) => void;
    onClickViewOneBenefits: () => void;
    onClickTicketItem: (props: IUpdateItemProp) => void;
    onClickDeleteTicketItem: (props: IUpdateItemProp) => void;
    eventsProceedButtonEvent: VoidFunction;
    showToastError: (error: string | undefined) => void;
    applyCouponWidget: IApplyCouponWidget | null;
    onClickApplyCoupon: VoidFunction;
    onClickRemoveCoupon: VoidFunction;
    onClickTermsAndConditions: (payload: { restaurantId: string; guests: number; dealCategory: string }) => void;
    onClickTor: (payload: { restaurantId: string; guests: number; dealCategory: string }) => void;
    pageErrorMeta?: {
        showError: boolean;
        errorPayload?: ICartPageErrorPayload;
    };
    isCustomFreeEventBookingFlow?: boolean;
    onImpressionDisclaimerBanner: (payload: IImpressionDisclaimerBanner) => void;
    onClickTermsAndConditionsForEventBooking: (payload: {
        restaurantId: string;
        guests: number;
        dealCategory: string;
    }) => void;
    onImpressionCashBackBenefitCallout: (props: IImpressionCashBackBenefitCallout) => void;
    rewardsWidgetData?: IRewardWidgetResponseData;
    itemRewardsWidgetData?: IRewardsWidget;
    cashbackPerc?: number;
    onRedeemCash?: (isRedeemed: boolean, amount: number) => void;
    ctaSubTextFromCallouts?: ICtaSubTextFromCallouts;
    _isTurboEnabled?: boolean;
    onImpressionTurboPayButtonReservation: (props: ITableBookingCartPage) => void;
    onClickTurboPayButtonReservation: (props: ITableBookingCartPage) => void;
    onClickChangePaymentMethod: (props: ITableBookingCartPage) => void;
    onClickSpecialRequest: () => void;
    onImpressionSpecialRequestHalfcard: () => void;
    onClickSubmitSpecialRequest: (instruction: string) => void;
    onClickDeleteSpecialRequest: () => void;
    onSubmitSpecialRequest: (instruction: string) => void;
    onClearSpecialRequestCart: () => void;
    partialPaymentWidget?: IPartialPaymentWidget;
    onPaymentOptionChange: (selectedId: string) => void;
    uniqueIdForCart?: string;
}

const LIST_ITEM_PADDING = {
    top: "12px",
    bottom: "12px",
    left: 0,
    right: 0,
};

interface IProceedCtaContainerProps {
    isSubscriptionUpsellApplicable: boolean;
    _isAnyItemUnavailable: boolean;
    ErrorFooterWidgetProps: {
        heading: string;
        subtext: string;
        ctaText: string;
        errorIcon: {
            cloudinaryId: string;
            width: string;
            height: string;
        };
        onCTAClick: VoidFunction;
    };
    onClickProceed: VoidFunction;
    ctaText: string;
    onImpressionCrucialComponents: (componentName: string) => void;
    ctaSubTextFromCallouts?: ICtaSubTextFromCallouts;
    highlightedFooterCallout?: IHighlightedFooterCallout;
}

const ProceedCtaContainer = (props: IProceedCtaContainerProps) => {
    const {
        isSubscriptionUpsellApplicable,
        _isAnyItemUnavailable,
        ErrorFooterWidgetProps,
        onClickProceed,
        ctaText,
        onImpressionCrucialComponents,
        ctaSubTextFromCallouts,
        highlightedFooterCallout,
    } = props;

    useEffectOnce(() => {
        if (!_isAnyItemUnavailable) {
            onImpressionCrucialComponents("proceed_cta_footer");
        }
    });

    const { icon: ctaIcon = "", text: ctaSubText = "" } = ctaSubTextFromCallouts || {};

    const showAlertBanner = useMemo(() => {
        return !isEmpty(highlightedFooterCallout);
    }, [highlightedFooterCallout]);

    return (
        <ProceedCtaWrapper>
            {showAlertBanner && (
                <AlertBanner
                    message={highlightedFooterCallout?.text || ""}
                    backgroundColor={highlightedFooterCallout?.backgroundColor as keyof IColors}
                    textColor={highlightedFooterCallout?.textColor as keyof IColors}
                />
            )}
            {isSubscriptionUpsellApplicable && (
                <CTASubText variant="V2.body3.neutral.medium" data-testid="proceed-cta-subtext">
                    {format(
                        CTA_BUTTON_TEXT,
                        ctaIcon ? String.fromCodePoint(Number(ctaIcon)) + " " : "",
                        ctaSubText || "",
                    )}
                </CTASubText>
            )}
            {_isAnyItemUnavailable ? (
                <ErrorFooterWidget {...ErrorFooterWidgetProps} />
            ) : (
                <FullWidthButton variant={"positive"} onClick={onClickProceed} data-testid="proceed-cta-button">
                    {ctaText}
                </FullWidthButton>
            )}
        </ProceedCtaWrapper>
    );
};

const Content = (props: IContentProps) => {
    const {
        isInitialCartLoading,
        isEventsBookingFlow,
        isCustomFreeEventBookingFlow,
        cartType,
        savingWidgetData,
        specialRequestWidgetData,
        headerData,
        eventTicketDetails,
        billAmount,
        skipPayment,
        billEntries,
        bookingTicketMeta,
        ticketData,
        dealCategory,
        superSavingBottomWidget,
        ids,
        selectedOfferId,
        selectedSlot,
        clickEventOnConfirmBookingTable,
        onImpressionBillDetails,
        onClickViewOneBenefits,
        onClickTicketItem,
        onClickDeleteTicketItem,
        eventsProceedButtonEvent,
        showToastError,
        applyCouponWidget,
        onClickApplyCoupon,
        onClickRemoveCoupon,
        pageErrorMeta,
        onClickTermsAndConditions,
        onClickTor,
        onImpressionDisclaimerBanner,
        onClickTermsAndConditionsForEventBooking,
        rewardsWidgetData,
        itemRewardsWidgetData,
        cashbackPerc,
        onRedeemCash,
        onImpressionCashBackBenefitCallout,
        ctaSubTextFromCallouts,
        _isTurboEnabled,
        onImpressionTurboPayButtonReservation,
        onClickTurboPayButtonReservation,
        onClickChangePaymentMethod,
        onClickSpecialRequest,
        onImpressionSpecialRequestHalfcard,
        onClickSubmitSpecialRequest,
        onClickDeleteSpecialRequest,
        onSubmitSpecialRequest,
        onClearSpecialRequestCart,
        partialPaymentWidget,
        onPaymentOptionChange,
        uniqueIdForCart,
        cartTypeFromLocalCart,
    } = props;

    const {
        cartState: { reviewedCart },
    } = useContext(DineoutCartContext);

    const handleClickTicketItem = useCallback(
        (item: ILocalCartItem) => {
            onClickTicketItem({
                restaurantId: ids.restaurantId || "",
                eventId: ids.eventId || "",
                ticketId: item.itemVariantId || "",
                numberOfGuests: item.quantity || 0,
                amount: item.itemPrice || 0,
            });
        },
        [ids.restaurantId, ids.eventId, onClickTicketItem],
    );

    const handleDeleteTicketItem = useCallback(
        (item: ILocalCartItem) => {
            onClickDeleteTicketItem({
                restaurantId: ids.restaurantId || "",
                eventId: ids.eventId || "",
                ticketId: item.itemVariantId || "",
                numberOfGuests: item.quantity || 0,
                amount: item.itemPrice || 0,
            });
        },
        [ids.restaurantId, ids.eventId, onClickDeleteTicketItem],
    );

    const { onItemQuantityUpdate, onDeleteItem, onRemoveUnavailableItems } = useDineoutCartItemActions({
        onClickTicketItem: handleClickTicketItem,
        onClickDeleteTicketItem: handleDeleteTicketItem,
        eventsProceedButtonEvent,
        cartType,
    });

    const onImpressionDisclaimerBannerHandler = useCallback(() => {
        if (!isEventsBookingFlow && !isCustomFreeEventBookingFlow) {
            onImpressionDisclaimerBanner({
                restaurantId: ids.restaurantId,
                billAmount: billAmount || 0,
                source: "",
                dealId: selectedOfferId || "",
            });
        }
    }, [
        billAmount,
        ids.restaurantId,
        isCustomFreeEventBookingFlow,
        isEventsBookingFlow,
        onImpressionDisclaimerBanner,
        selectedOfferId,
    ]);

    // Unavailable items count in case of event booking flow
    const unavailableItemsCount = useMemo(() => {
        return getUnavailableItemsCount(eventTicketDetails);
    }, [eventTicketDetails]);

    const {
        showPopup: showCouponPopup,
        onCloseModal: onCloseCouponPopup,
        couponModalData,
    } = useContext(UpdateCartContext);
    const ErrorFooterWidgetProps = useMemo(() => {
        return {
            heading: format(UNAVAILABLE_ITEMS_CTA_SUBTEXT.heading, unavailableItemsCount.toString()),
            subtext: UNAVAILABLE_ITEMS_CTA_SUBTEXT.subtext,
            ctaText: UNAVAILABLE_ITEMS_CTA_SUBTEXT.ctaText,
            errorIcon: UNAVAILABLE_ITEMS_CTA_SUBTEXT.errorIcon,
            onCTAClick: onRemoveUnavailableItems,
        };
    }, [onRemoveUnavailableItems, unavailableItemsCount]);

    const [colorMode, setColorMode] = useState<"dark" | "light" | undefined>(undefined);

    useEffect(() => {
        if (document.getElementsByTagName("html")[0].classList.contains("dark")) {
            setColorMode("dark");
            return;
        }
        if (document.getElementsByTagName("html")[0].classList.contains("light")) {
            setColorMode("light");
            return;
        }
    }, []);

    const _isAnyItemUnavailable = useMemo(() => isAnyItemUnavailable(eventTicketDetails), [eventTicketDetails]);
    const _areAllItemsUnavailable: boolean = useMemo(
        () => areAllItemsUnavailable(eventTicketDetails),
        [eventTicketDetails],
    );
    const { bookingDetails, offerDetails, restaurantDetails } = bookingTicketMeta || {};
    const { ticketTermsAndCondition, ticketThingsToRemember } = ticketData || {};
    const [showBenefitsHalfcard, setShowBenefitsHalfcard] = useState(false);
    const [showUnavailableItemsPrompt, setShowUnavailableItemsPrompt] = useState(_isAnyItemUnavailable);
    const [isTurboPayWidgetHidden, setIsTurboPayWidgetHidden] = useState(false);
    const { isCartValidationInProgress, onClickProceed, saveReferrerDataToSessionStorage } = useDineoutCartActions({
        cartType: cartTypeFromLocalCart,
        skipPayment,
        clickEvent: clickEventOnConfirmBookingTable,
        setSoftError: showToastError,
    });
    const [paymentUniqueId, setPaymentUniqueId] = useState("");
    const {
        onImpressionCashBackEarned,
        onImpressionCashBackToggle,
        onClickCashBackToggle,
        onImpressionErrorHalfCard,
        onClickErrorHalfCard,
    } = useDineoutBookingAnalytics();
    const isRewardRedeemed = useMemo(() => getIsRewardRedeemed(rewardsWidgetData), [rewardsWidgetData]);
    const redeemableRewards = useMemo(() => getRedeemableRewards(rewardsWidgetData), [rewardsWidgetData]);
    const rewardsTogglePayload = useMemo(() => getRewardsTogglePayload(rewardsWidgetData), [rewardsWidgetData]);
    const tableReservationEarnedDetails = useMemo(() => {
        if (isEmpty(itemRewardsWidgetData)) {
            return undefined;
        }
        return {
            title: itemRewardsWidgetData?.title ?? "",
            logo: itemRewardsWidgetData?.logo ?? "",
            backgroundImage: itemRewardsWidgetData?.backgroundImage ?? "",
        };
    }, [itemRewardsWidgetData]);
    const highlightedFooterCallout = useMemo(() => {
        return partialPaymentWidget?.highlightedFooterCallout;
    }, [partialPaymentWidget]);

    const dinecashEarningsNudge = partialPaymentWidget?.dinecashEarningsNudge;

    const radioCardOptions = useMemo(() => {
        if (!partialPaymentWidget?.options) return [];
        return partialPaymentWidget.options.map((option) => ({
            ...option,
            nudge:
                option.id === PAYMENT_OPTION_IDS.PARTIAL_PAYMENT &&
                dinecashEarningsNudge &&
                !isEmpty(dinecashEarningsNudge.text) ? (
                    <DinecashEarningsNudge nudge={dinecashEarningsNudge} />
                ) : undefined,
        }));
    }, [partialPaymentWidget?.options, dinecashEarningsNudge]);

    const handleClickThingsToRemember = useCallback(() => {
        if (!isEventsBookingFlow && !isCustomFreeEventBookingFlow) {
            onClickTor({
                restaurantId: ids.restaurantId,
                guests: bookingDetails?.noOfGuests || 0,
                dealCategory: dealCategory || "",
            });
        }
    }, [
        isEventsBookingFlow,
        isCustomFreeEventBookingFlow,
        onClickTor,
        ids.restaurantId,
        bookingDetails?.noOfGuests,
        dealCategory,
    ]);
    const onToggleSwitch = useCallback(() => {
        onClickCashBackToggle({
            eventId: ids.eventId || "",
            dealCategory: dealCategory || "",
            restaurantId: Number(ids.restaurantId) || 0,
            billAmount: billAmount || 0,
            ...rewardsTogglePayload,
            toggleState: isRewardRedeemed ? "off" : "on",
            cashbackPerc: cashbackPerc || 0,
            couponApplied: Boolean(applyCouponWidget?.coupon),
            couponDiscount: applyCouponWidget?.appliedCouponDiscount || 0,
            couponDisabled: !applyCouponWidget,
        });
        onRedeemCash?.(!isRewardRedeemed, redeemableRewards);
    }, [
        onClickCashBackToggle,
        ids.eventId,
        ids.restaurantId,
        dealCategory,
        billAmount,
        rewardsTogglePayload,
        isRewardRedeemed,
        cashbackPerc,
        applyCouponWidget,
        onRedeemCash,
        redeemableRewards,
    ]);

    useEffect(() => {
        if (skipPayment !== false) return;
        if (_isTurboEnabled && !isEmpty(uniqueIdForCart)) {
            setPaymentUniqueId(createAndPersistPaymentLink());
        }
    }, [skipPayment, _isTurboEnabled, uniqueIdForCart]);

    const turboReservationEventPayload = useMemo(
        () => ({
            restaurantId: ids.restaurantId,
            selectedGuestCount: bookingDetails?.noOfGuests || 0,
            selectedDate: bookingDetails?.date || "",
            selectedSlot: selectedSlot?.reservationTime.toString() || "",
            selectedOfferId: selectedOfferId || "",
            selectedOfferText: offerDetails?.title || "",
            selectedSlotId: selectedSlot?.id || 0,
            selectedDealCategory: dealCategory || "",
            billDetails: getBillEntriesList(billEntries),
            buttonVariant: "button",
        }),
        [
            ids.restaurantId,
            bookingDetails?.noOfGuests,
            bookingDetails?.date,
            selectedSlot?.reservationTime,
            selectedSlot?.id,
            selectedOfferId,
            offerDetails?.title,
            dealCategory,
            billEntries,
        ],
    );

    const onImpressionTurboPayButtonReservationHandler = useCallback(
        (_paymentMethod: string, _billAmount: string, _fromFallback?: boolean, groupName?: string) => {
            onImpressionTurboPayButtonReservation({ ...turboReservationEventPayload, paymentType: groupName });
        },
        [onImpressionTurboPayButtonReservation, turboReservationEventPayload],
    );

    const onClickTurboPayButtonReservationHandler = useCallback(
        (_paymentMethod: string, _billAmount: string, groupName?: string) => {
            // Save referrer data to session storage for turbo payment flow
            saveReferrerDataToSessionStorage();

            onClickTurboPayButtonReservation({ ...turboReservationEventPayload, paymentType: groupName });
        },
        [onClickTurboPayButtonReservation, turboReservationEventPayload, saveReferrerDataToSessionStorage],
    );

    const onClickChangePaymentMethodHandler = useCallback(
        (_paymentMethod: string, _billAmount: string, _fromFallback?: boolean, groupName?: string) => {
            onClickChangePaymentMethod({ ...turboReservationEventPayload, paymentType: groupName });
        },
        [onClickChangePaymentMethod, turboReservationEventPayload],
    );

    const handleClickTermsAndConditions = useCallback(() => {
        if (!isEventsBookingFlow && !isCustomFreeEventBookingFlow) {
            onClickTermsAndConditions({
                restaurantId: ids.restaurantId,
                guests: bookingDetails?.noOfGuests || 0,
                dealCategory: dealCategory || "",
            });
        } else {
            onClickTermsAndConditionsForEventBooking({
                restaurantId: ids.restaurantId,
                guests: bookingDetails?.noOfGuests || 0,
                dealCategory: dealCategory || "",
            });
        }
    }, [
        isEventsBookingFlow,
        isCustomFreeEventBookingFlow,
        onClickTermsAndConditions,
        ids.restaurantId,
        bookingDetails?.noOfGuests,
        dealCategory,
        onClickTermsAndConditionsForEventBooking,
    ]);

    const getListItemProps = useMemo(
        () => (title: string, clickAnalyticsEvent: () => void) => ({
            title,
            expandable: true,
            primaryIcon: {
                iconName: "chevronDown16" as IconName,
            },
            onClick: () => clickAnalyticsEvent(),
            expandedIcon: {
                iconName: "chevronUp16" as IconName,
            },
            titleVariant: "V2.body1.neutral.bold" as keyof ITypography,
            customPadding: LIST_ITEM_PADDING,
        }),
        [],
    );

    const onErrorTurboPay = useCallback(() => {
        setIsTurboPayWidgetHidden(true);
        recordNREvent({
            eventType: NEWRELIC_TABLES.TURBO_PAY_WIDGET_HIDDEN_DUE_TO_ERROR,
            isTurboEnabled: String(_isTurboEnabled),
        });
    }, [_isTurboEnabled]);

    const getListItems = useCallback(
        (ticketTermsAndCondition, ticketThingsToRemember) => {
            const listItems = [];
            if (!isEmpty(ticketThingsToRemember?.listOfThingsToRemember)) {
                listItems.push({
                    ...getListItemProps(CART_PAGE_TEXTS.ThingsToRemember, handleClickThingsToRemember),
                    children: <ThingsToRemember thingsToRemember={ticketThingsToRemember?.listOfThingsToRemember} />,
                });
            }
            if (!isEmpty(ticketTermsAndCondition?.tnc)) {
                let tncHeader;
                if (isEventsBookingFlow) {
                    tncHeader = CART_PAGE_TEXTS.EventTnC;
                } else if (!isEmpty(offerDetails?.title)) {
                    tncHeader = CART_PAGE_TEXTS.TnCHeaderWithOffer;
                } else {
                    tncHeader = CART_PAGE_TEXTS.TnCHeaderWithoutOffer;
                }
                listItems.push({
                    ...getListItemProps(tncHeader, handleClickTermsAndConditions),
                    children: <OfferTnC tnc={ticketTermsAndCondition?.tnc} />,
                });
            }
            return listItems;
        },
        [
            getListItemProps,
            handleClickTermsAndConditions,
            handleClickThingsToRemember,
            isEventsBookingFlow,
            offerDetails?.title,
        ],
    );

    const ctaText = getCTAText(billAmount, skipPayment, isEventsBookingFlow);

    const popupData = getPopupData(_areAllItemsUnavailable);

    const {
        name: restaurantName = "",
        locality: restaurantLocality = "",
        city: restaurantCity = "",
    } = restaurantDetails || {};

    const restaurantAddress = `${restaurantLocality} , ${restaurantCity}`;

    const upsellWidgetProps = useMemo(() => getUpsellWidgetProps(superSavingBottomWidget), [superSavingBottomWidget]);

    const downloadBannerProps = useMemo(() => {
        return {
            title: DOWNLOAD_APP_BANNER_TITLE,
            subTitle: DOWNLOAD_APP_BANNER_SUBTITLE,
        };
    }, []);

    const onImpressionCrucialComponents = useCallback((componentName: string) => {
        recordNREvent({
            eventType: NEWRELIC_TABLES.TABLE_RESERVATION_CART_P0_COMPONENT_IMPRESSION,
            componentName,
        });
    }, []);

    const onImpressionCashBackBenefitCalloutHandler = useCallback(
        (isVisible: boolean) => {
            if (isVisible) {
                onImpressionCashBackBenefitCallout({
                    bannerText: itemRewardsWidgetData?.title || "",
                    dealCategory: dealCategory || "",
                    restaurantId: ids.restaurantId,
                });
            }
        },
        [onImpressionCashBackBenefitCallout, ids.restaurantId, dealCategory, itemRewardsWidgetData?.title],
    );

    const onImpressionErrorPage = useCallback(() => {
        recordNREvent({
            eventType: NEWRELIC_TABLES.TABLE_RESERVATION_CART_ERROR_PAGE_IMPRESSION,
            title: pageErrorMeta?.errorPayload?.title || "",
            message: pageErrorMeta?.errorPayload?.message || "",
        });
    }, [pageErrorMeta?.errorPayload?.message, pageErrorMeta?.errorPayload?.title]);

    const handleClickSwiggyOneWidgetCTA = () => {
        const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "";
        // Only trigger onClickViewOneBenefits analytics for table booking flow, not for event booking flows
        // to avoid using incorrect screenName "DO-ReviewBooking" in event booking contexts
        if (!isEventsBookingFlow && !isCustomFreeEventBookingFlow) {
            onClickViewOneBenefits();
        }
        if (isWebPlatform(platform)) {
            setShowBenefitsHalfcard(true);
            return;
        }

        const benefitsHalfcardDeeplink = upsellWidgetProps?.itemData?.meta?.cta?.link;
        if (benefitsHalfcardDeeplink) {
            navigateToDeeplink(appendThemeToExternalWidgetUrl(benefitsHalfcardDeeplink));
        }
    };

    const hideSwiggyOneBenefitsHalfcard = () => {
        setShowBenefitsHalfcard(false);
    };

    const handleCloseUnavailableItemsPrompt = useCallback(() => {
        setShowUnavailableItemsPrompt(false);
    }, []);

    const onImpressionBillDetailsHandler = useCallback(() => {
        const slotDetails = selectedSlot || ({} as ICartSelectedSlot);

        const billDetails = getBillEntriesList(billEntries);
        const { restaurantId, eventId } = ids;
        const eventTotalGuestCount = eventTicketDetails?.itemList?.reduce(
            (acc: number, ticket: { quantity: number }) => acc + ticket.quantity,
            0,
        );
        const guests = isEventsBookingFlow ? eventTotalGuestCount : bookingTicketMeta?.bookingDetails?.noOfGuests;
        const ticketDetails = getTicketDetailsIdList(eventTicketDetails?.itemList);

        onImpressionBillDetails({
            restaurantID: restaurantId || "",
            offerId: selectedOfferId || "",
            dealCategory: dealCategory || "",
            guests: String(guests) || "",
            slotDetails: String(slotDetails.reservationTime) || "",
            billDetails,
            eventId,
            ticketDetails,
            isEventsBookingFlow: isEventsBookingFlow || isCustomFreeEventBookingFlow,
        });
    }, [
        selectedSlot,
        billEntries,
        ids,
        eventTicketDetails?.itemList,
        onImpressionBillDetails,
        selectedOfferId,
        dealCategory,
        bookingTicketMeta?.bookingDetails?.noOfGuests,
        isEventsBookingFlow,
        isCustomFreeEventBookingFlow,
    ]);

    const isSubscriptionUpsellApplicable = !isEmpty(superSavingBottomWidget);
    const shouldShowTurboPayWidget = _isTurboEnabled && !isTurboPayWidgetHidden && !skipPayment && !isEmpty(billAmount);

    const hasCartError = useMemo(() => {
        const statusCode = reviewedCart?.statusCode;
        return statusCode != null && statusCode !== CART_STATUS_CODES.SUCCESS;
    }, [reviewedCart?.statusCode]);

    const onCartErrorAction = useCallback(async (action: string) => {
        switch (action.toLowerCase()) {
            case CART_ERROR_CTA.CLOSE_WEBVIEW:
                closeWebview();
                break;
            default:
                break;
        }
    }, []);

    const renderFooterContent = useCallback(() => {
        if (hasCartError) {
            return (
                <FooterContainer showBackground={hasCartError}>
                    <CartErrorWidget
                        action={onCartErrorAction}
                        statusCode={reviewedCart?.statusCode || 0}
                        statusMessage={reviewedCart?.statusMessage || ""}
                        ctaText={reviewedCart?.ctaText || ""}
                        ctaAction={reviewedCart?.ctaActionV2}
                        titleMessage={reviewedCart?.statusTitle || ""}
                        secondaryCtaText={reviewedCart?.secondaryCtaText || ""}
                        secondaryCtaAction={reviewedCart?.secondaryCtaAction}
                        getErrorWidgetAttrs={getErrorWidgetAttrs}
                        onImpression={onImpressionErrorHalfCard}
                        onClickAnalytics={onClickErrorHalfCard}
                    />
                </FooterContainer>
            );
        }

        if (shouldShowTurboPayWidget) {
            return (
                <ProceedCtaWrapper
                    showTurboPayment={_isTurboEnabled}
                    showAlertBanner={!isEmpty(highlightedFooterCallout)}
                >
                    {!isEmpty(highlightedFooterCallout) && (
                        <AlertBanner
                            isTurboVariantEnabled={_isTurboEnabled}
                            message={highlightedFooterCallout?.text || ""}
                            showIcon={true}
                            backgroundColor={highlightedFooterCallout?.backgroundColor as keyof IColors}
                            textColor={highlightedFooterCallout?.textColor as keyof IColors}
                            iconFillColor={highlightedFooterCallout?.textColor as keyof IColors}
                        />
                    )}
                    {paymentUniqueId ? (
                        <TurboPayment
                            uniqueId={paymentUniqueId}
                            onPaymentMethodChange={onClickProceed}
                            onError={onErrorTurboPay}
                            showLoader={false}
                            showDiscountedValueOnSlider={false}
                            isTurboV2={true}
                            hideEducationComms={false}
                            onTurboPayClickEvent={onClickTurboPayButtonReservationHandler}
                            onPaymentMethodChangeClickEvent={onClickChangePaymentMethodHandler}
                            forceColorMode={colorMode}
                            onTurboPayImpressionEvent={onImpressionTurboPayButtonReservationHandler}
                        />
                    ) : (
                        <TurboPaymentWidgetShimmerV2 />
                    )}
                </ProceedCtaWrapper>
            );
        }

        return (
            <ProceedCtaContainer
                isSubscriptionUpsellApplicable={isSubscriptionUpsellApplicable}
                _isAnyItemUnavailable={_isAnyItemUnavailable}
                ErrorFooterWidgetProps={ErrorFooterWidgetProps}
                onClickProceed={onClickProceed}
                ctaText={ctaText}
                onImpressionCrucialComponents={onImpressionCrucialComponents}
                ctaSubTextFromCallouts={ctaSubTextFromCallouts}
                highlightedFooterCallout={highlightedFooterCallout}
            />
        );
    }, [
        hasCartError,
        reviewedCart,
        onCartErrorAction,
        onImpressionErrorHalfCard,
        onClickErrorHalfCard,
        shouldShowTurboPayWidget,
        _isTurboEnabled,
        highlightedFooterCallout,
        paymentUniqueId,
        onClickProceed,
        onErrorTurboPay,
        onClickTurboPayButtonReservationHandler,
        onClickChangePaymentMethodHandler,
        colorMode,
        onImpressionTurboPayButtonReservationHandler,
        isSubscriptionUpsellApplicable,
        _isAnyItemUnavailable,
        ErrorFooterWidgetProps,
        ctaText,
        onImpressionCrucialComponents,
        ctaSubTextFromCallouts,
    ]);

    useEffect(() => {
        if (shouldShowTurboPayWidget) {
            recordNREvent({
                eventType: NEWRELIC_TABLES.TURBO_PAY_WIDGET_SHOWN,
                isTurboEnabled: String(_isTurboEnabled),
            });
        }
    }, [shouldShowTurboPayWidget, _isTurboEnabled]);

    if (isInitialCartLoading) {
        return <TableReservationShimmer />;
    }

    if (pageErrorMeta?.showError && !isEmpty(pageErrorMeta?.errorPayload)) {
        return <GenericError {...pageErrorMeta.errorPayload} pageImpressionEvent={onImpressionErrorPage} />;
    }

    if (!bookingDetails || !offerDetails) return null;
    return (
        <ContentContainer>
            {savingWidgetData && (
                <SavingWidgetWrapper>
                    <SavingWidget props={savingWidgetData} onImpression={onImpressionDisclaimerBannerHandler} />
                </SavingWidgetWrapper>
            )}
            {isEventsBookingFlow && !isEmpty(eventTicketDetails?.itemList) ? (
                <TicketDetailsWrapper>
                    {!isEmpty(headerData) && <Header {...headerData} />}
                    <CartItemListRenderer
                        {...eventTicketDetails}
                        itemList={eventTicketDetails?.itemList || []}
                        date={eventTicketDetails?.date || ""}
                        time={eventTicketDetails?.time || ""}
                        itemCountTitle={eventTicketDetails?.itemCountTitle || ""}
                        updateItemQuantity={onItemQuantityUpdate}
                        onRemoveItem={onDeleteItem}
                        enableSoldOutComs={true}
                    />
                    <Popup
                        isOpen={showUnavailableItemsPrompt}
                        closeOnOverlayClick
                        onClose={handleCloseUnavailableItemsPrompt}
                    >
                        <ErrorWidget
                            title={popupData.title}
                            subTitle={popupData.subTitle}
                            secondaryButton={{
                                onClick: handleCloseUnavailableItemsPrompt,
                                text: popupData.secondaryButtonText,
                            }}
                            primaryButton={{
                                onClick: closeWebview,
                                text: popupData.primaryButtonText,
                            }}
                        />
                    </Popup>
                </TicketDetailsWrapper>
            ) : (
                <div data-testid={CART_TEST_IDS.TABLE_BOOKING_INFO}>
                    <ElementVisibilitySensor onVisibilityChange={onImpressionCashBackBenefitCalloutHandler}>
                        <TableBookingInfoTicket
                            bookingDetails={bookingDetails}
                            restaurantDetails={{
                                address: restaurantAddress,
                                name: restaurantName,
                            }}
                            offerDetails={{
                                ...offerDetails,
                                chipSrc: getCloudinaryUrl(offerDetails?.chipSrc) || "",
                            }}
                            downloadAppBannerProps={downloadBannerProps}
                            earnedDetails={tableReservationEarnedDetails}
                        />
                    </ElementVisibilitySensor>
                </div>
            )}
            <SpecialRequest
                specialRequestWidgetData={specialRequestWidgetData}
                onClickSpecialRequest={onClickSpecialRequest}
                onImpressionSpecialRequestHalfcard={onImpressionSpecialRequestHalfcard}
                onClickSubmitSpecialRequest={onClickSubmitSpecialRequest}
                onClickDeleteSpecialRequest={onClickDeleteSpecialRequest}
                onSubmitSpecialRequest={onSubmitSpecialRequest}
                onClearSpecialRequestCart={onClearSpecialRequestCart}
            />
            {isEventsBookingFlow && (!skipPayment || isRewardRedeemed) && !isEmpty(rewardsWidgetData) && (
                <RewardsWidget
                    rewardsWidget={rewardsWidgetData}
                    onToggleSwitch={onToggleSwitch}
                    isSelected={isRewardRedeemed}
                    eventId={ids.eventId || ""}
                    isScenesFlow
                    dealCategory={dealCategory || ""}
                    restaurantId={Number(ids.restaurantId) || 0}
                    billAmount={billAmount || 0}
                    cashbackPerc={cashbackPerc || 0}
                    couponApplied={Boolean(applyCouponWidget?.coupon)}
                    couponDiscount={applyCouponWidget?.appliedCouponDiscount || 0}
                    onImpressionCashBackEarned={onImpressionCashBackEarned}
                    onImpressionCashBackToggle={onImpressionCashBackToggle}
                />
            )}
            {isSubscriptionUpsellApplicable && (
                <SwiggyOneWidgetWrapper>
                    <SwiggyOneWidget {...upsellWidgetProps} onItemClick={handleClickSwiggyOneWidgetCTA} />
                </SwiggyOneWidgetWrapper>
            )}

            {applyCouponWidget && (
                <CouponSection
                    subtitle={applyCouponWidget.subtitle}
                    title={applyCouponWidget.title}
                    subtitleLogo={applyCouponWidget.subtitleLogo}
                    subtitleColour={applyCouponWidget.subtitleColour}
                    appliedCoupon={applyCouponWidget.coupon}
                    appliedCouponValue={applyCouponWidget.appliedCouponDiscount}
                    onClickApplyCoupon={onClickApplyCoupon}
                    onClickRemoveCoupon={onClickRemoveCoupon}
                ></CouponSection>
            )}

            {/* Not to show the bill details for the free-reservation */}
            {!skipPayment && (
                <BillDetails
                    billEntries={billEntries}
                    title={BILL_DETAILS_TEXTS.BillDetails}
                    onImpression={onImpressionBillDetailsHandler}
                />
            )}

            {partialPaymentWidget && !isEmpty(partialPaymentWidget.options) && (
                <PartialPaymentSection>
                    <RadioCardGroup
                        options={radioCardOptions}
                        onChange={onPaymentOptionChange}
                        name={PARTIAL_PAYMENT_RADIO_GROUP.NAME}
                        title={PARTIAL_PAYMENT_RADIO_GROUP.TITLE}
                    />
                </PartialPaymentSection>
            )}
            <DividerWrapper />
            <ListViewWithBorder>
                <ListView
                    items={getListItems(ticketTermsAndCondition, ticketThingsToRemember)}
                    expendableType={ExpendableType.MULTIPLE}
                    background="canvas_Primary"
                />
            </ListViewWithBorder>

            {renderFooterContent()}

            <Popup
                isOpen={isCartValidationInProgress}
                closeOnOverlayClick={false}
                isBackgroundTransparent
                isNotBorderColor
                isNotBoxShadow
                width={VALIDATION_LOADER_POPUP_WIDTH}
            >
                <ValidationLoadingContainer>
                    <Loader
                        size="large"
                        type="secondary"
                        foregroundColor="primary"
                        backgroundColor="background_Disabled"
                        isDisabled={false}
                    />
                    <Text variant="V2.body1.neutral.bold" color="text_High_Emphasis">
                        {VALIDATION_LOADER_TEXT}
                    </Text>
                </ValidationLoadingContainer>
            </Popup>
            {showCouponPopup ? (
                <CartModal
                    data={couponModalData as CouponMessageCard}
                    closeModal={onCloseCouponPopup}
                    closeIconBgColor={isDineoutCheckoutTheme() ? DINEOUT_CART_MODAL_CLOSE_ICON_BG : undefined}
                />
            ) : null}
            {/* This is a workaround implementation of oneBottomSheet since we require this halfcard to load in web cart where opening another web in halfcard is not supported yet with web-bridge */}
            <BenefitsHalfcard
                hideSwiggyOneBenefitsHalfcard={hideSwiggyOneBenefitsHalfcard}
                showBenefitsHalfcard={showBenefitsHalfcard}
            ></BenefitsHalfcard>
        </ContentContainer>
    );
};

export default memo(Content);
